import { SentryError } from '@ifixit/sentry';
import { createContext, useMemo, useContext } from 'react';
import type { PropsWithChildren } from 'react';
import type {
   CountryCode,
   LanguageCode,
   Sdk as StorefrontClient,
   VisitorConsent,
} from './generated/sdk';
import { getClientShopifyStorefrontSdk, type VisitorTokens } from './sdk';

export { type Sdk as StorefrontClient } from './generated/sdk';

const Context = createContext<{
   client: StorefrontClient;
   country: CountryCode;
   currencyCode: string;
   language: LanguageCode;
   shopDomain: string;
   storeCode: string;
   storefrontAccessToken: string;
   storeUrl?: string;
   cartUrl?: string;
   /** Whether cart requests carry the visitor's consent, so checkoutUrl does too. */
   sendsVisitorConsent: boolean;
} | null>(null);

export type ShopifyStorefrontContextProviderProps = PropsWithChildren<{
   country: CountryCode;
   currencyCode: string;
   language: LanguageCode;
   shopDomain: string;
   storeCode: string;
   storefrontAccessToken: string;
   storeUrl?: string;
   cartUrl?: string;
   getVisitorTokens?: () => VisitorTokens;
   getVisitorConsent?: () => VisitorConsent | null;
}>;

export function ShopifyStorefrontProvider({
   country,
   currencyCode,
   language,
   shopDomain,
   storeCode,
   storefrontAccessToken,
   storeUrl,
   cartUrl,
   getVisitorTokens,
   getVisitorConsent,
   children,
}: ShopifyStorefrontContextProviderProps) {
   const client = useMemo(
      () => ({
         client: getClientShopifyStorefrontSdk({
            shopDomain,
            storefrontAccessToken,
            getVisitorTokens,
            getVisitorConsent,
         }),
         country,
         currencyCode,
         language,
         shopDomain,
         storeCode,
         storefrontAccessToken,
         storeUrl,
         cartUrl,
         sendsVisitorConsent: Boolean(getVisitorConsent),
      }),
      [
         country,
         currencyCode,
         language,
         shopDomain,
         storeCode,
         storefrontAccessToken,
         storeUrl,
         cartUrl,
         getVisitorTokens,
         getVisitorConsent,
      ]
   );
   return <Context.Provider value={client}>{children}</Context.Provider>;
}

export function useShopifyStorefrontClient() {
   const client = useContext(Context);
   if (client == null) {
      throw new SentryError(
         'useShopifyStorefrontClient must be used within a ShopifyStorefrontProvider'
      );
   }
   return client;
}
